import React, { createContext, useState, useContext, useEffect } from 'react';
import { getMockUsers, getMockUser, setMockUser, clearMockUser } from '../mock';
import { 
  saveUserForOffline, 
  getOfflineUserCredentials, 
  getOfflineUserData,
  simpleHash 
} from '../services/offlineStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [masterUser, setMasterUser] = useState(null);

  useEffect(() => {
    // Initialize auth state from localStorage on app load
    const storedUser = localStorage.getItem('currentUser');
    const storedToken = localStorage.getItem('token');
    
    console.log('AuthContext init - Token exists:', !!storedToken, 'User exists:', !!storedUser);
    
    if (storedUser && storedToken) {
      try {
        const user = JSON.parse(storedUser);
        console.log('AuthContext - User restored from localStorage:', user.email);
        setCurrentUser(user);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        // Clear corrupted data
        localStorage.removeItem('currentUser');
        localStorage.removeItem('token');
      }
    } else {
      console.log('AuthContext - No stored session found');
    }
    
    // Check if impersonating
    const impersonatingData = localStorage.getItem('impersonating');
    if (impersonatingData) {
      try {
        const data = JSON.parse(impersonatingData);
        setImpersonating(true);
        setMasterUser(data.masterUser);
      } catch (error) {
        console.error('Failed to parse impersonating data:', error);
        localStorage.removeItem('impersonating');
      }
    }
    
    setLoading(false);
  }, []);

  const checkSubscriptionStatus = (user) => {
    // Master user doesn't need subscription
    if (user.role === 'master') {
      return { hasAccess: true, status: 'master' };
    }

    // Master Panel user doesn't need subscription
    if (user.role === 'master_panel') {
      return { hasAccess: true, status: 'master_panel' };
    }

    // OFFLINE MODE: Be more lenient with subscription checks
    // Allow access if user has an active subscription cached (trust the cached data)
    if (user.isOfflineSession) {
      if (user.subscription && user.subscription.status === 'active') {
        // Check if subscription has expiry date and if it's still valid
        const expiryDate = user.subscription.expiryDate;
        if (expiryDate) {
          const expiry = new Date(expiryDate);
          const now = new Date();
          // Give 1 day grace period for offline users (clock sync issues)
          const gracePeriod = 24 * 60 * 60 * 1000; // 1 day in ms
          if (expiry.getTime() + gracePeriod > now.getTime()) {
            return { hasAccess: true, status: 'active', expiryDate, isOffline: true };
          }
        } else {
          // No expiry date means unlimited access (e.g., lifetime plan)
          return { hasAccess: true, status: 'active', isOffline: true };
        }
      }
      // Offline with valid subscription data - allow read-only access
      return { hasAccess: true, status: 'offline_readonly', isOffline: true };
    }

    // Staff, manager, and stakeholder members inherit admin's subscription
    // The backend already provides the inherited subscription in the user object
    if (user.role === 'staff' || user.role === 'manager' || user.role === 'stakeholder' || user.role === 'receptionist') {
      // Use the subscription from the user object (inherited from admin by backend)
      if (user.subscription) {
        const { status, expiryDate } = user.subscription;
        
        if (status === 'active') {
          // Check if not expired
          if (!expiryDate || new Date(expiryDate) > new Date()) {
            return { hasAccess: true, status: 'active', expiryDate };
          }
        }
      }
      // Staff with no subscription - should not happen if backend is correct
      return { hasAccess: false, status: 'no_subscription', message: 'Admin subscription required' };
    }

    // Admin subscription check
    if (user.role === 'admin') {
      if (!user.subscription) {
        return { hasAccess: false, status: 'pending', message: 'Subscription pending approval' };
      }

      const { status, expiryDate } = user.subscription;

      if (status === 'pending') {
        return { hasAccess: false, status: 'pending', message: 'Subscription pending approval' };
      }

      if (status === 'rejected') {
        return { hasAccess: false, status: 'rejected', message: 'Subscription request rejected' };
      }

      if (status === 'expired' || (expiryDate && new Date(expiryDate) < new Date())) {
        return { hasAccess: false, status: 'expired', message: 'Subscription expired' };
      }

      if (status === 'active') {
        return { hasAccess: true, status: 'active', expiryDate };
      }
    }

    return { hasAccess: false, status: 'unknown' };
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      // Clear any existing cache before login to ensure fresh data
      sessionStorage.clear();
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      if (!API_URL) {
        throw new Error('REACT_APP_BACKEND_URL environment variable is not configured');
      }
      
      // Check if we're offline
      if (!navigator.onLine) {
        return await offlineLogin(email, password);
      }
      
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.detail || 'Login failed' };
      }

      const data = await response.json();
      
      // Check if Master Admin or Master Panel requires OTP verification
      if (data.requiresOTP) {
        return {
          success: false,
          requiresOTP: true,
          email: data.email,
          message: data.message,
          loginType: data.loginType || 'master' // Pass through login type
        };
      }
      
      // Check if user has subscription issues
      if (!data.access_token && data.user) {
        // No subscription or pending approval
        return {
          success: false,
          error: data.user.subscription?.status === 'pending' 
            ? 'Your subscription is pending approval from admin' 
            : 'Please select a subscription plan',
          user: data.user
        };
      }

      // Store token and user
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      
      // Save credentials for offline login (hash the password)
      const passwordHash = simpleHash(password);
      await saveUserForOffline(email, passwordHash, data.user);
      
      // Check subscription status
      const subscriptionCheck = checkSubscriptionStatus(data.user);
      
      setCurrentUser(data.user);
      
      return { 
        success: true, 
        user: data.user,
        subscription: subscriptionCheck,
        token: data.access_token
      };
    } catch (error) {
      console.error('Login error:', error);
      
      // If network error and we're offline, try offline login
      if (!navigator.onLine || error.message?.includes('fetch')) {
        return await offlineLogin(email, password);
      }
      
      return { success: false, error: 'Network error. Please try again.' };
    }
  };
  
  // Offline login using cached credentials
  const offlineLogin = async (email, password) => {
    try {
      const cachedCredentials = await getOfflineUserCredentials(email);
      
      if (!cachedCredentials) {
        return { 
          success: false, 
          error: 'No offline data found. Please login online first to enable offline access.',
          isOffline: true
        };
      }
      
      // Verify password hash
      const passwordHash = simpleHash(password);
      if (passwordHash !== cachedCredentials.passwordHash) {
        return { success: false, error: 'Invalid credentials', isOffline: true };
      }
      
      // Get cached user data
      const cachedUser = await getOfflineUserData(email);
      
      if (!cachedUser) {
        return { 
          success: false, 
          error: 'User data not found in offline cache',
          isOffline: true
        };
      }
      
      // Mark user as offline-authenticated
      const offlineUser = {
        ...cachedUser,
        isOfflineSession: true
      };
      
      // Store in localStorage for session
      localStorage.setItem('currentUser', JSON.stringify(offlineUser));
      localStorage.setItem('token', 'offline-session-token');
      localStorage.setItem('isOfflineSession', 'true');
      
      setCurrentUser(offlineUser);
      
      const subscriptionCheck = checkSubscriptionStatus(offlineUser);
      
      return { 
        success: true, 
        user: offlineUser,
        subscription: subscriptionCheck,
        isOffline: true,
        message: 'Logged in offline. View-only mode enabled.'
      };
    } catch (error) {
      console.error('Offline login error:', error);
      return { 
        success: false, 
        error: 'Failed to authenticate offline. Please try again.',
        isOffline: true
      };
    }
  };
  
  const verifyMasterOTP = async (email, otp, loginType = 'master') => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      if (!API_URL) {
        throw new Error('REACT_APP_BACKEND_URL environment variable is not configured');
      }
      
      // Determine endpoint based on login type
      const endpoint = loginType === 'master_panel' 
        ? `${API_URL}/api/auth/verify-master-panel-otp`
        : `${API_URL}/api/auth/verify-master-otp`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ email, otp }),
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.detail || 'OTP verification failed' };
      }

      const data = await response.json();
      
      // Store token and user
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      
      // Check subscription status
      const subscriptionCheck = checkSubscriptionStatus(data.user);
      
      setCurrentUser(data.user);
      
      return { 
        success: true, 
        user: data.user,
        subscription: subscriptionCheck,
        token: data.access_token,
        loginType: loginType
      };
    } catch (error) {
      console.error('Master OTP verification error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    // Clear state
    setCurrentUser(null);
    setImpersonating(false);
    setMasterUser(null);
    clearMockUser();
    
    // Clear localStorage completely
    localStorage.clear();
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear service worker caches
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
    } catch (error) {
      console.error('Error clearing caches on logout:', error);
    }
    
  };

  const loginAs = async (userId) => {
    if (!currentUser?.isMaster) {
      return { success: false, error: 'Only master can impersonate users' };
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      if (!API_URL) {
        return { success: false, error: 'API URL not configured' };
      }
      const masterToken = localStorage.getItem('token');
      
      if (!masterToken) {
        return { success: false, error: 'No authentication token found' };
      }
      
      const response = await fetch(`${API_URL}/api/master/login-as/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${masterToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to impersonate user');
      }

      const data = await response.json();
      const targetUser = data.user;
      const newToken = data.access_token;
      
      // Store master user info and original token for return
      setMasterUser(currentUser);
      setImpersonating(true);
      localStorage.setItem('impersonating', JSON.stringify({
        masterUser: currentUser,
        masterToken: masterToken,  // Store master's token for return
        targetUserId: userId
      }));
      
      // Switch to target user's token - this is critical for data access!
      if (newToken) {
        localStorage.setItem('token', newToken);
      }
      
      // Update current user state
      setCurrentUser(targetUser);
      setMockUser(targetUser);
      localStorage.setItem('currentUser', JSON.stringify(targetUser));
      
      return { success: true, user: targetUser };
    } catch (error) {
      console.error('Login as error:', error);
      return { success: false, error: error.message || 'Failed to login as user' };
    }
  };

  const returnToMaster = () => {
    if (!impersonating || !masterUser) {
      return { success: false, error: 'Not impersonating' };
    }

    // Restore master's token from stored impersonating data
    const impersonatingData = localStorage.getItem('impersonating');
    if (impersonatingData) {
      try {
        const parsedData = JSON.parse(impersonatingData);
        if (parsedData.masterToken) {
          localStorage.setItem('token', parsedData.masterToken);
        }
      } catch (e) {
        console.error('Error parsing impersonating data:', e);
      }
    }

    setCurrentUser(masterUser);
    setMockUser(masterUser);
    localStorage.setItem('currentUser', JSON.stringify(masterUser));
    setImpersonating(false);
    setMasterUser(null);
    localStorage.removeItem('impersonating');
    
    return { success: true };
  };

  const updateProfile = (updatedData) => {
    const updatedUser = { ...currentUser, ...updatedData };
    setCurrentUser(updatedUser);
    setMockUser(updatedUser);
  };

  const refreshUser = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      if (!API_URL || !token) {
        // Fallback to mock data if no API
        const users = getMockUsers();
        const user = users.find(u => u.id === currentUser?.id);
        if (user) {
          const userWithoutPassword = { ...user };
          delete userWithoutPassword.password;
          setCurrentUser(userWithoutPassword);
          setMockUser(userWithoutPassword);
          localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
        }
        return;
      }

      // Fetch fresh user data from API
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          setCurrentUser(data.user);
          setMockUser(data.user);
          localStorage.setItem('currentUser', JSON.stringify(data.user));
        }
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const value = {
    currentUser,
    login,
    verifyMasterOTP,
    logout,
    updateProfile,
    checkSubscriptionStatus,
    refreshUser,
    loginAs,
    returnToMaster,
    impersonating,
    masterUser,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};